import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import CommonService from '../../services/common.service';
import { API_URLS } from '../../constants/api-urls';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/seller_cm_rfqs.scss';
import '../../assets/styles/submit_quotation_to_buyer.scss';
import LinkedLogisticsPanel from '../../components/moduleLogistics/LinkedLogisticsPanel';
import { useProductDetailsMaster } from '../../hooks/useProductDetailsMaster';
import { useFactoryDetailsMaster } from '../../hooks/useFactoryDetailsMaster';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';

function getObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

function getDisplayValue(v) {
  if (v == null) return '—';
  if (Array.isArray(v)) {
    const out = v.map((x) => String(x || '').trim()).filter(Boolean);
    return out.length ? out.join(', ') : '—';
  }
  const s = String(v).trim();
  return s || '—';
}

function formatYesNo(v) {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim();
  if (!s) return '—';
  const lc = s.toLowerCase();
  if (lc === 'true' || lc === 'yes' || lc === 'y' || lc === '1') return 'Yes';
  if (lc === 'false' || lc === 'no' || lc === 'n' || lc === '0') return 'No';
  return s;
}

function findOption(options, value) {
  if (!Array.isArray(options)) return null;
  const v = value != null ? String(value).trim() : '';
  if (!v) return null;
  return options.find((o) => String(o?.value) === v) || null;
}

function findMultiOptions(options, value) {
  if (!Array.isArray(options)) return [];
  const values = Array.isArray(value)
    ? value.map((x) => String(x))
    : String(value || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  if (values.length === 0) return [];
  const set = new Set(values);
  return options.filter((o) => set.has(String(o?.value)));
}

function toArrayValues(value) {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (value == null) return [];
  return String(value).split(',').map((s) => s.trim()).filter(Boolean);
}

function toBool(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (value == null) return fallback;
  const s = String(value).trim().toLowerCase();
  if (!s) return fallback;
  if (['true', 'yes', 'y', '1'].includes(s)) return true;
  if (['false', 'no', 'n', '0'].includes(s)) return false;
  return fallback;
}

function hasRequiredValue(value) {
  if (value == null) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return String(value).trim().length > 0;
}

const mapSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  singleValue: (base) => ({ ...base, color: '#1f2937' }),
};

function YesNoSwitch({ checked, onToggle, id, disabled = false }) {
  return (
    <button
      type="button"
      id={id}
      className={`sp-switch ${checked ? 'on' : 'off'}`}
      role="switch"
      aria-checked={checked}
      onClick={() => {
        if (disabled) return;
        if (typeof onToggle === 'function') onToggle(!checked);
      }}
      disabled={disabled}
    >
      <span className="sp-switch-label">{checked ? 'Yes' : 'No'}</span>
      <span className="sp-switch-thumb" />
    </button>
  );
}

const PRICING_MODEL_OPTIONS = [
  { value: 'Per Piece ( Unit Price )', label: 'Per Piece ( Unit Price )' },
  { value: 'Per Batch/Lot', label: 'Per Batch/Lot' },
  { value: 'PerHour ( Machine / Labour )', label: 'PerHour ( Machine / Labour )' },
  { value: 'Per Day / Per Shift', label: 'Per Day / Per Shift' },
  { value: 'Monthly Lease / Retainer', label: 'Monthly Lease / Retainer' },
  { value: 'Cost + ( BOM Pass Through + Markup )', label: 'Cost + ( BOM Pass Through + Markup )' },
  { value: 'Fixed project Fee', label: 'Fixed project Fee' },
];

const PRICE_ESCALATION_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'CPI Index', label: 'CPI Index' },
  { value: 'LME ( Metals )', label: 'LME ( Metals )' },
  { value: 'Resin Index ( Chem/ Plastics)', label: 'Resin Index ( Chem/ Plastics)' },
  { value: 'Energy Surcharge', label: 'Energy Surcharge' },
  { value: 'FX Clause', label: 'FX Clause' },
];

const ADD_ON_CATEGORY_OPTIONS = [
  { value: 'Setup/Changeover per run', label: 'Setup/Changeover per run' },
  { value: 'Packaging materials', label: 'Packaging materials' },
  { value: 'QA/Inspection (FAI/PPAP)', label: 'QA/Inspection (FAI/PPAP)' },
  { value: 'Certification documents', label: 'Certification documents' },
  { value: 'Freight & Logistics', label: 'Freight & Logistics' },
  { value: 'Duties/Taxes', label: 'Duties/Taxes' },
  { value: 'Artwork/tool texturing', label: 'Artwork/tool texturing' },
  { value: 'Expedite/Rush fee', label: 'Expedite/Rush fee' },
  { value: 'Disposal/Waste handling', label: 'Disposal/Waste handling' },
];

const TOOLING_OWNERSHIP_OPTIONS = [
  { value: 'Buyer', label: 'Buyer' },
  { value: 'Supplier', label: 'Supplier' },
  { value: 'Shared', label: 'Shared' },
];

const JURISDICTION_OPTIONS = [
  { value: 'India', label: 'India' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'UAE', label: 'UAE' },
  { value: 'Uk', label: 'Uk' },
  { value: 'USA', label: 'USA' },
  { value: 'EU ( Specify)', label: 'EU ( Specify)' },
];

const DELIVERY_METHOD_OPTIONS = [
  { value: 'Direct Delivery', label: 'Direct Delivery' },
  { value: 'Warehouse Delivery', label: 'Warehouse Delivery' },
  { value: 'Pickup', label: 'Pickup' },
];

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'];

const UNIT_OF_MEASUREMENT_OPTIONS = [
  { value: 'Nos', label: 'Nos' },
  { value: 'Kg', label: 'Kg' },
  { value: 'Ton', label: 'Ton' },
  { value: 'Ltr', label: 'Ltr' },
  { value: 'Mtr', label: 'Mtr' },
];

const BATCH_LOT_UOM_OPTIONS = [
  { value: 'Batch', label: 'Batch' },
  { value: 'Lot', label: 'Lot' },
];

const PER_HOUR_UOM_OPTIONS = [
  { value: 'Machine Hour', label: 'Machine Hour' },
  { value: 'Labour Hour', label: 'Labour Hour' },
  { value: 'Line Hour', label: 'Line Hour' },
];

const PER_DAY_SHIFT_UOM_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'Shift', label: 'Shift' },
];

const PROJECT_RETAINER_UOM_OPTIONS = [
  { value: 'PCS', label: 'PCS' },
  { value: 'Set', label: 'Set' },
  { value: 'Pair', label: 'Pair' },
  { value: 'Kg', label: 'Kg' },
  { value: 'g', label: 'g' },
  { value: 'ton', label: 'ton' },
  { value: 'mm', label: 'mm' },
  { value: 'sq.m', label: 'sq.m' },
];

const SAMPLE_TYPE_OPTIONS = [
  { value: 'Product Sample', label: 'Product Sample' },
  { value: 'Material Sample', label: 'Material Sample' },
  { value: 'Prototype', label: 'Prototype' },
  { value: 'Digital Mockup', label: 'Digital Mockup' },
];

const SAMPLE_COST_OPTIONS = [
  { value: 'Free', label: 'Free' },
  { value: 'Chargeable', label: 'Chargeable' },
];

const SAMPLE_SHIPPING_MODE_OPTIONS = [
  { value: 'Courier', label: 'Courier' },
  { value: 'Air', label: 'Air' },
  { value: 'Surface', label: 'Surface' },
  { value: 'Customer Pickup', label: 'Customer Pickup' },
];

const SAMPLE_RETURN_POLICY_OPTIONS = [
  { value: 'Returnable', label: 'Returnable' },
  { value: 'Non- Returnable', label: 'Non- Returnable' },
  { value: 'On Approval Basis', label: 'On Approval Basis' },
];

const WARRANTY_PERIOD_OPTIONS = [
  { value: 'Returnable', label: 'Returnable' },
  { value: 'Non- Returnable', label: 'Non- Returnable' },
  { value: 'On Approval Basis', label: 'On Approval Basis' },
];

function createEmptyTierBreakRow() {
  return {
    tierLabel: '',
    minQuantity: '',
    unitPrice: '',
    currency: 'USD',
    remarks: '',
  };
}

export default function SellerContractManufacturerRfqQuotePage() {
  const { rfqId: rfqIdParam } = useParams();
  const navigate = useNavigate();
  const rfqId = parseInt(rfqIdParam, 10);

  const { attributeUnitOptions, dimensionUnitOptions } = useProductDetailsMaster();
  const { currencyOptions } = useCurrencyMaster();
  const {
    modeOfTransportOptions,
    paymentMethodOptions,
    paymentScheduleOptions,
    shippingTermsOptions,
  } = useFactoryDetailsMaster();

  const [loading, setLoading] = useState(true);
  const [rfqMeta, setRfqMeta] = useState(null);
  const [payload, setPayload] = useState(null);
  const [existingQuote, setExistingQuote] = useState(null);

  const [saving, setSaving] = useState(false);
  const [rfqDetailsExpanded, setRfqDetailsExpanded] = useState(true);
  const [buyerInfoExpanded, setBuyerInfoExpanded] = useState(true);
  const [projectOverviewExpanded, setProjectOverviewExpanded] = useState(false);
  const [productsExpanded, setProductsExpanded] = useState(false);
  const [technicalExpanded, setTechnicalExpanded] = useState(false);
  const [qualityExpanded, setQualityExpanded] = useState(false);
  const [packagingExpanded, setPackagingExpanded] = useState(false);
  const [commercialExpanded, setCommercialExpanded] = useState(false);
  const [basePriceExpanded, setBasePriceExpanded] = useState(false);
  const [tierBreaksExpanded, setTierBreaksExpanded] = useState(false);
  const [sampleExpanded, setSampleExpanded] = useState(false);
  const [warrantyExpanded, setWarrantyExpanded] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [complianceAccepted, setComplianceAccepted] = useState(false);
  const [commercialEdit, setCommercialEdit] = useState({
    preferredPricingModel: '',
    paymentMethod: '',
    unitOfMeasurement: '',
    paymentTerms: '',
    priceValidityDays: '',
    incoterms: '',
    priceEscalationClause: '',
    addOnCategories: [],
    liquidatedDamages: '',
    priceNegotiable: false,
    toolingNrePossible: false,
    toolingNre: '',
    toolingOwnership: '',
    jurisdiction: '',
    deliveryMethods: '',
    portOfShipping: '',
    expectedDispatchDate: '',
  });
  const [basePriceEdit, setBasePriceEdit] = useState({
    price: '',
    quantity: '',
    cgst: '',
    sgst: '',
    igst: '',
    cess: '',
    vat: '',
    otherTaxes: '',
  });
  const [tierBreaksEdit, setTierBreaksEdit] = useState([createEmptyTierBreakRow()]);
  const [sampleQuoteEdit, setSampleQuoteEdit] = useState({
    sampleOffered: false,
    sampleType: '',
    sampleCost: '',
    samplePrice: '',
    sampleCurrency: '',
    dispatchLeadTimeDays: '',
    sampleShippingMode: '',
    sampleReturnPolicy: '',
    sampleRemarks: '',
    warranty: '',
    warrantyPeriod: '',
    warrantyRemarks: '',
    guarantee: '',
    guaranteePeriod: '',
    guaranteeRemarks: '',
  });
  const [quoteAttachmentNames, setQuoteAttachmentNames] = useState([]);
  const [quoteAttachmentsMeta, setQuoteAttachmentsMeta] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (Number.isNaN(rfqId) || rfqId <= 0) {
      toast.error('Invalid RFQ');
      navigate('/seller/cm-rfqs');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_RFQ_DETAIL(rfqId));
        const data = res?.data ?? {};
        if (cancelled) return;
        setRfqMeta(data?.rfq || null);
        setPayload(data?.rfq?.payload || null);
        const q = data?.quotation;
        if (q?.exists && q?.payload) {
          setExistingQuote(q.payload);
        }
      } catch (e) {
        const m = e?.response?.data?.message || e?.message || 'Unable to load RFQ';
        toast.error(m);
        navigate('/seller/cm-rfqs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rfqId, navigate]);

  const project = getObj(payload?.project);
  const business = getObj(payload?.business);
  const technical = getObj(payload?.technical);
  const quality = getObj(payload?.quality);
  const packaging = getObj(payload?.packaging);
  const commercial = getObj(payload?.commercial);
  const basePrice = getObj(payload?.basePrice);
  const sample = getObj(payload?.sample);
  const products = Array.isArray(payload?.products) ? payload.products : [];
  const tierBreaks = Array.isArray(payload?.tierBreaks) ? payload.tierBreaks : [];

  useEffect(() => {
    const qCommercial = getObj(existingQuote?.commercial);
    const src = Object.keys(qCommercial).length > 0 ? qCommercial : commercial;
    setCommercialEdit({
      preferredPricingModel: String(src.preferredPricingModel || '').trim(),
      paymentMethod: String(src.paymentMethod || '').trim(),
      unitOfMeasurement: String(src.unitOfMeasurement || '').trim(),
      paymentTerms: String(src.paymentTerms || '').trim(),
      priceValidityDays: src.priceValidityDays != null ? String(src.priceValidityDays) : '',
      incoterms: String(src.incoterms || '').trim(),
      priceEscalationClause: String(src.priceEscalationClause || '').trim(),
      addOnCategories: toArrayValues(src.addOnCategories),
      liquidatedDamages: String(src.liquidatedDamages || '').trim(),
      priceNegotiable: toBool(src.priceNegotiable, true),
      toolingNrePossible: toBool(src.toolingNrePossible, true),
      toolingNre: String(src.toolingNre || '').trim(),
      toolingOwnership: String(src.toolingOwnership || '').trim(),
      jurisdiction: String(src.jurisdiction || '').trim(),
      deliveryMethods: String(src.deliveryMethods || '').trim(),
      portOfShipping: String(src.portOfShipping || '').trim(),
      expectedDispatchDate: String(src.expectedDispatchDate || '').trim(),
    });
  }, [commercial, existingQuote]);

  useEffect(() => {
    const qBase = getObj(existingQuote?.basePrice);
    const srcBase = Object.keys(qBase).length > 0 ? qBase : basePrice;
    setBasePriceEdit({
      price: srcBase.price != null ? String(srcBase.price) : '',
      quantity: srcBase.quantity != null ? String(srcBase.quantity) : (srcBase.moq != null ? String(srcBase.moq) : ''),
      cgst: srcBase.cgst != null ? String(srcBase.cgst) : '',
      sgst: srcBase.sgst != null ? String(srcBase.sgst) : '',
      igst: srcBase.igst != null ? String(srcBase.igst) : '',
      cess: srcBase.cess != null ? String(srcBase.cess) : '',
      vat: srcBase.vat != null ? String(srcBase.vat) : '',
      otherTaxes: srcBase.otherTaxes != null ? String(srcBase.otherTaxes) : '',
    });

    const qTierBreaks = Array.isArray(existingQuote?.tierBreaks) ? existingQuote.tierBreaks : [];
    const srcTierBreaks = qTierBreaks.length > 0 ? qTierBreaks : tierBreaks;
    if (srcTierBreaks.length > 0) {
      setTierBreaksEdit(
        srcTierBreaks.map((row) => ({
          tierLabel: row?.tierLabel != null ? String(row.tierLabel) : '',
          minQuantity: row?.minQuantity != null ? String(row.minQuantity) : '',
          unitPrice: row?.unitPrice != null ? String(row.unitPrice) : '',
          currency: row?.currency != null ? String(row.currency) : 'USD',
          remarks: row?.remarks != null ? String(row.remarks) : '',
        }))
      );
    } else {
      setTierBreaksEdit([createEmptyTierBreakRow()]);
    }
  }, [basePrice, tierBreaks, existingQuote]);

  useEffect(() => {
    const qSample = getObj(existingQuote?.sample);
    const srcSample = Object.keys(qSample).length > 0 ? qSample : sample;
    setSampleQuoteEdit({
      sampleOffered: toBool(srcSample.sampleOffered, false),
      sampleType: String(srcSample.sampleType || '').trim(),
      sampleCost: String(srcSample.sampleCost || '').trim(),
      samplePrice: String(srcSample.samplePrice || '').trim(),
      sampleCurrency: String(srcSample.sampleCurrency || '').trim(),
      dispatchLeadTimeDays: srcSample.dispatchLeadTimeDays != null ? String(srcSample.dispatchLeadTimeDays) : '',
      sampleShippingMode: String(srcSample.sampleShippingMode || '').trim(),
      sampleReturnPolicy: String(srcSample.sampleReturnPolicy || '').trim(),
      sampleRemarks: String(srcSample.sampleRemarks || '').trim(),
      warranty: String(srcSample.warranty || '').trim(),
      warrantyPeriod: String(srcSample.warrantyPeriod || '').trim(),
      warrantyRemarks: String(srcSample.warrantyRemarks || '').trim(),
      guarantee: String(srcSample.guarantee || '').trim(),
      guaranteePeriod: String(srcSample.guaranteePeriod || '').trim(),
      guaranteeRemarks: String(srcSample.guaranteeRemarks || '').trim(),
    });
  }, [sample, existingQuote]);

  useEffect(() => {
    const terms = existingQuote?.declarationTermsAccepted === true || existingQuote?.declarations?.terms === true;
    const compliance = existingQuote?.declarationComplianceAccepted === true || existingQuote?.declarations?.compliance === true;
    setTermsAccepted(terms);
    setComplianceAccepted(compliance);
  }, [existingQuote]);

  useEffect(() => {
    const raw = Array.isArray(existingQuote?.quoteAttachments)
      ? existingQuote.quoteAttachments
      : (Array.isArray(existingQuote?.quoteAttachmentsMeta) ? existingQuote.quoteAttachmentsMeta : []);
    const normalized = raw
      .map((a) => ({
        name: String(a?.name || '').trim(),
        type: String(a?.type || '').trim(),
        size: Number(a?.size) || 0,
      }))
      .filter((a) => a.name);
    setQuoteAttachmentsMeta(normalized);
    setQuoteAttachmentNames(normalized.map((a) => a.name));
  }, [existingQuote]);

  const uomLabelByValue = useMemo(() => {
    const map = new Map();
    (Array.isArray(attributeUnitOptions) ? attributeUnitOptions : []).forEach((opt) => {
      const k = opt?.value != null ? String(opt.value).trim() : '';
      const v = opt?.label != null ? String(opt.label).trim() : '';
      if (k && v) map.set(k, v);
    });
    return map;
  }, [attributeUnitOptions]);

  const resolveUomLabel = (uomValue) => {
    if (uomValue == null) return '—';
    const raw = String(uomValue).trim();
    if (!raw) return '—';
    return uomLabelByValue.get(raw) || raw;
  };

  const transportLabelByValue = useMemo(() => {
    const map = new Map();
    (Array.isArray(modeOfTransportOptions) ? modeOfTransportOptions : []).forEach((opt) => {
      const k = opt?.value != null ? String(opt.value).trim() : '';
      const v = opt?.label != null ? String(opt.label).trim() : '';
      if (k && v) map.set(k, v);
    });
    return map;
  }, [modeOfTransportOptions]);

  const resolveTransportLabel = (transportValue) => {
    if (transportValue == null) return '—';
    const raw = String(transportValue).trim();
    if (!raw) return '—';
    return transportLabelByValue.get(raw) || raw;
  };

  const paymentMethodLabelByValue = useMemo(() => {
    const map = new Map();
    (Array.isArray(paymentMethodOptions) ? paymentMethodOptions : []).forEach((opt) => {
      const k = opt?.value != null ? String(opt.value).trim() : '';
      const v = opt?.label != null ? String(opt.label).trim() : '';
      if (k && v) map.set(k, v);
    });
    return map;
  }, [paymentMethodOptions]);

  const paymentTermsLabelByValue = useMemo(() => {
    const map = new Map();
    (Array.isArray(paymentScheduleOptions) ? paymentScheduleOptions : []).forEach((opt) => {
      const k = opt?.value != null ? String(opt.value).trim() : '';
      const v = opt?.label != null ? String(opt.label).trim() : '';
      if (k && v) map.set(k, v);
    });
    return map;
  }, [paymentScheduleOptions]);

  const incotermsLabelByValue = useMemo(() => {
    const map = new Map();
    (Array.isArray(shippingTermsOptions) ? shippingTermsOptions : []).forEach((opt) => {
      const k = opt?.value != null ? String(opt.value).trim() : '';
      const v = opt?.label != null ? String(opt.label).trim() : '';
      if (k && v) map.set(k, v);
    });
    return map;
  }, [shippingTermsOptions]);

  const currencyMappedOptions = useMemo(
    () => (Array.isArray(currencyOptions) && currencyOptions.length > 0 ? currencyOptions : CURRENCIES.map((c) => ({ value: c, label: c }))),
    [currencyOptions]
  );

  const unitOfMeasurementMapped = useMemo(
    () => (Array.isArray(dimensionUnitOptions) && dimensionUnitOptions.length > 0 ? dimensionUnitOptions : UNIT_OF_MEASUREMENT_OPTIONS),
    [dimensionUnitOptions]
  );

  const unitOfMeasurementOptions = useMemo(() => {
    if (commercialEdit.preferredPricingModel === 'Per Batch/Lot') return BATCH_LOT_UOM_OPTIONS;
    if (commercialEdit.preferredPricingModel === 'PerHour ( Machine / Labour )') return PER_HOUR_UOM_OPTIONS;
    if (commercialEdit.preferredPricingModel === 'Per Day / Per Shift') return PER_DAY_SHIFT_UOM_OPTIONS;
    if (['Monthly Lease / Retainer', 'Cost + ( BOM Pass Through + Markup )', 'Fixed project Fee'].includes(commercialEdit.preferredPricingModel)) {
      return PROJECT_RETAINER_UOM_OPTIONS;
    }
    return unitOfMeasurementMapped;
  }, [commercialEdit.preferredPricingModel, unitOfMeasurementMapped]);

  const resolvePaymentMethodLabel = (value) => {
    if (value == null) return '—';
    const raw = String(value).trim();
    if (!raw) return '—';
    return paymentMethodLabelByValue.get(raw) || raw;
  };

  const resolvePaymentTermsLabel = (value) => {
    if (value == null) return '—';
    const raw = String(value).trim();
    if (!raw) return '—';
    return paymentTermsLabelByValue.get(raw) || raw;
  };

  const resolveIncotermsLabel = (value) => {
    if (value == null) return '—';
    const raw = String(value).trim();
    if (!raw) return '—';
    return incotermsLabelByValue.get(raw) || raw;
  };


  const handleTierBreakChange = (index, key, value) => {
    setTierBreaksEdit((prev) => prev.map((row, idx) => (idx === index ? { ...row, [key]: value } : row)));
  };

  const addTierBreakRow = () => {
    setTierBreaksEdit((prev) => [...prev, createEmptyTierBreakRow()]);
  };

  const removeTierBreakRow = (index) => {
    setTierBreaksEdit((prev) => {
      if (!Array.isArray(prev) || prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const handleQuoteAttachmentSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    const meta = files.map((f) => ({
      name: f.name,
      type: f.type || '',
      size: Number(f.size) || 0,
    }));
    setQuoteAttachmentsMeta(meta);
    setQuoteAttachmentNames(meta.map((f) => f.name));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const localErrors = {};
    if (!hasRequiredValue(commercialEdit.preferredPricingModel)) localErrors['commercial.preferredPricingModel'] = 'Preferred pricing model is required';
    if (!hasRequiredValue(commercialEdit.paymentMethod)) localErrors['commercial.paymentMethod'] = 'Payment method is required';
    if (!hasRequiredValue(commercialEdit.unitOfMeasurement)) localErrors['commercial.unitOfMeasurement'] = 'Unit of measurement is required';
    if (!hasRequiredValue(commercialEdit.paymentTerms)) localErrors['commercial.paymentTerms'] = 'Payment terms are required';
    if (!hasRequiredValue(commercialEdit.incoterms)) localErrors['commercial.incoterms'] = 'Incoterms are required';

    if (!hasRequiredValue(basePriceEdit.price)) localErrors['basePrice.price'] = 'Enter price is required';
    if (!hasRequiredValue(basePriceEdit.quantity)) localErrors['basePrice.quantity'] = 'Quantity / MOQ is required';

    const sanitizedTierBreaks = tierBreaksEdit
      .map((row) => ({
        tierLabel: String(row.tierLabel || '').trim(),
        minQuantity: String(row.minQuantity || '').trim(),
        unitPrice: String(row.unitPrice || '').trim(),
        currency: String(row.currency || 'USD').trim(),
        remarks: String(row.remarks || '').trim(),
      }))
      .filter((row) => row.tierLabel || row.minQuantity || row.unitPrice || row.remarks);

    if (sanitizedTierBreaks.length === 0) {
      localErrors['tierBreaks'] = 'Please add at least one preferred tier break';
    } else {
      sanitizedTierBreaks.forEach((row, idx) => {
        if (!hasRequiredValue(row.tierLabel)) localErrors[`tierBreaks.${idx}.tierLabel`] = 'Tier is required';
        if (!hasRequiredValue(row.minQuantity)) localErrors[`tierBreaks.${idx}.minQuantity`] = 'Minimum quantity is required';
        if (!hasRequiredValue(row.unitPrice)) localErrors[`tierBreaks.${idx}.unitPrice`] = 'Target unit price is required';
        if (!hasRequiredValue(row.currency)) localErrors[`tierBreaks.${idx}.currency`] = 'Currency is required';
      });
    }

    if (sampleQuoteEdit.sampleOffered) {
      if (!hasRequiredValue(sampleQuoteEdit.sampleType)) localErrors['sample.sampleType'] = 'Sample type is required';
      if (!hasRequiredValue(sampleQuoteEdit.sampleCost)) localErrors['sample.sampleCost'] = 'Sample cost is required';
      if (!hasRequiredValue(sampleQuoteEdit.samplePrice)) localErrors['sample.samplePrice'] = 'Sample price is required';
      if (!hasRequiredValue(sampleQuoteEdit.dispatchLeadTimeDays)) localErrors['sample.dispatchLeadTimeDays'] = 'Dispatch lead time is required';
      if (!hasRequiredValue(sampleQuoteEdit.sampleShippingMode)) localErrors['sample.sampleShippingMode'] = 'Sample shipping mode is required';
      if (!hasRequiredValue(sampleQuoteEdit.sampleReturnPolicy)) localErrors['sample.sampleReturnPolicy'] = 'Sample return policy is required';
    }

    if (!hasRequiredValue(sampleQuoteEdit.warranty)) localErrors['sample.warranty'] = 'Warranty is required';
    if (!hasRequiredValue(sampleQuoteEdit.warrantyPeriod)) localErrors['sample.warrantyPeriod'] = 'Warranty period is required';
    if (!hasRequiredValue(sampleQuoteEdit.guarantee)) localErrors['sample.guarantee'] = 'Guarantee is required';
    if (!hasRequiredValue(sampleQuoteEdit.guaranteePeriod)) localErrors['sample.guaranteePeriod'] = 'Guarantee period is required';

    if (!termsAccepted) localErrors.declarationTermsAccepted = 'Please accept terms declaration';
    if (!complianceAccepted) localErrors.declarationComplianceAccepted = 'Please accept compliance declaration';

    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      toast.error(Object.values(localErrors)[0] || 'Please provide all required fields');
      return;
    }

    setFieldErrors({});
    setSaving(true);
    try {
      const body = {
        commercial: commercialEdit,
        basePrice: basePriceEdit,
        tierBreaks: sanitizedTierBreaks,
        sample: sampleQuoteEdit,
        quoteAttachments: quoteAttachmentsMeta,
        declarationTermsAccepted: termsAccepted,
        declarationComplianceAccepted: complianceAccepted,
      };
      const resp = await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_RFQ_QUOTE(rfqId), body);
      const quoteApproval = resp?.data?.quoteApproval ?? resp?.quoteApproval;
      if (quoteApproval?.mode === 'pending') {
        toast.info(
          'Quotation submitted for P2P contract manufacturing sale approval. The buyer can view it after approvers complete the workflow.',
        );
      } else {
        toast.success(existingQuote ? 'Quotation updated' : 'Quotation submitted');
      }
      navigate('/seller/cm-rfqs');
    } catch (err) {
      const apiFieldErrors = err?.response?.data?.fieldErrors;
      if (apiFieldErrors && typeof apiFieldErrors === 'object') {
        setFieldErrors(apiFieldErrors);
      }
      const m = err?.response?.data?.message || err?.message || 'Failed to submit quotation';
      toast.error(m);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-muted">Loading…</p>;
  }

  const createdDate = rfqMeta?.createdAt || rfqMeta?.created_at || rfqMeta?.submittedDate;
  const rfqStatusValue = (() => {
    const raw = existingQuote ? 'quoted' : (rfqMeta?.quoteStatus || rfqMeta?.status || 'open');
    const normalized = String(raw).trim().toLowerCase();
    if (normalized === 'responded' || normalized === 'quoted' || normalized === 'submitted') return 'Quoted';
    return 'Open';
  })();
  const deliveryAddress = [
    business.deliveryAddress || commercial.shippingAddress,
    business.city || commercial.shippingCity,
    business.state || commercial.shippingState,
    business.country || commercial.shippingCountry,
    (business.pincode || commercial.shippingPincode) ? `PIN: ${business.pincode || commercial.shippingPincode}` : '',
  ].filter(Boolean).join(', ');

  return (
    <div className="submit-quotation">
      <div className="container-fluid">
        {rfqId && (
          <LinkedLogisticsPanel
            sourceModule="contract_manufacturing"
            sourceRecordType="manufacturing_rfq"
            sourceRecordId={String(rfqId)}
            linkPurpose="fulfillment"
            title="CM RFQ logistics (seller)"
          />
        )}

        <div className="sq-header">
          <div className="sq-top-heading-card">
            <div className="sq-top-heading-left">
              <button type="button" className="sq-top-back-btn" onClick={() => navigate('/seller/cm-rfqs')} aria-label="Back to RFQ list">
                ←
              </button>
              <div>
                <h1 className="sq-top-title">RFQ Details & Quotation</h1>
                <p className="sq-top-subtitle">Review RFQ requirements and submit your quotation</p>
              </div>
            </div>
            <span className="sq-top-rfq-badge">{getDisplayValue(rfqMeta?.displayId || `RFQ-${rfqId}`)}</span>
          </div>
        </div>

        <div className="sq-rfq-details-standalone">
          <div className="sq-rfq-details-card">
            <div className="sq-rfq-details-header" onClick={() => setRfqDetailsExpanded((v) => !v)}>
              <div className="sq-rfq-details-header-left">
                <h3 className="sq-section-title">RFQ Details</h3>
              </div>
              <span className={`sq-rfq-details-chevron ${rfqDetailsExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {rfqDetailsExpanded && (
              <div className="sq-rfq-details-content">
                <div className="sq-rfq-details-grid">
                  <div className="sq-rfq-detail-item"><label>RFQ ID</label><p className="sq-value">{getDisplayValue(rfqMeta?.displayId || rfqId)}</p></div>
                  <div className="sq-rfq-detail-item"><label>RFQ Date</label><p className="sq-value">{getDisplayValue(createdDate)}</p></div>
                  <div className="sq-rfq-detail-item"><label>RFQ Status</label><div><span className="sq-status sq-status-open">{rfqStatusValue}</span></div></div>
                  <div className="sq-rfq-detail-item"><label>Expected Start</label><p className="sq-value">{getDisplayValue(project.expectedStartDate)}</p></div>
                  <div className="sq-rfq-detail-item"><label>Payment Terms</label><p className="sq-value">{resolvePaymentTermsLabel(commercial.paymentTerms)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setBuyerInfoExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                </svg>
                <h3 className="sq-section-title">Business Information</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${buyerInfoExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {buyerInfoExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Company Name</label><p className="sq-value">{getDisplayValue(business.companyName)}</p></div>
                  <div className="sq-buyer-info-item"><label>Contact Name</label><p className="sq-value">{getDisplayValue(business.contactName)}</p></div>
                  <div className="sq-buyer-info-item"><label>Email</label><p className="sq-value">{getDisplayValue(business.email)}</p></div>
                  <div className="sq-buyer-info-item"><label>Phone</label><p className="sq-value">{getDisplayValue(business.phone)}</p></div>
                  <div className="sq-buyer-info-item"><label>Technical Contact</label><p className="sq-value">{getDisplayValue(business.technicalContact)}</p></div>
                  <div className="sq-buyer-info-item"><label>Commercial Contact</label><p className="sq-value">{getDisplayValue(business.commercialContact)}</p></div>
                  <div className="sq-buyer-info-item"><label>Preferred Communication</label><p className="sq-value">{getDisplayValue(business.preferredCommunication || business.preferredContactMethod || business.preferredCommunicationMethod)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setProjectOverviewExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M3 3h18v18H3z"></path>
                  <path d="M9 9h6"></path>
                  <path d="M9 13h6"></path>
                </svg>
                <h3 className="sq-section-title">Project Overview</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${projectOverviewExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {projectOverviewExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Project Title</label><p className="sq-value">{getDisplayValue(project.title)}</p></div>
                  <div className="sq-buyer-info-item"><label>Industry Type</label><p className="sq-value">{getDisplayValue(project.industryType)}</p></div>
                  <div className="sq-buyer-info-item"><label>Manufacturing Process</label><p className="sq-value">{getDisplayValue(project.manufacturingProcessFacilities || project.manufacturingProcess || project.process || technical.manufacturingProcess)}</p></div>
                  <div className="sq-buyer-info-item"><label>NDA Required</label><p className="sq-value">{getDisplayValue(project.ndaRequired || technical.ndaRequired)}</p></div>
                  <div className="sq-buyer-info-item"><label>Expected Start Date</label><p className="sq-value">{getDisplayValue(project.expectedStartDate)}</p></div>
                  <div className="sq-buyer-info-item"><label>Frequency of Manufacturing</label><p className="sq-value">{getDisplayValue(project.frequencyOfManufacturing || project.frequency)}</p></div>
                  <div className="sq-buyer-info-item"><label>Estimated Order Volume</label><p className="sq-value">{getDisplayValue(project.estimatedOrderVolume || project.orderVolume)}</p></div>
                  <div className="sq-buyer-info-item"><label>Estimated Annual Usage</label><p className="sq-value">{getDisplayValue(project.estimatedAnnualUsage)}</p></div>
                  <div className="sq-buyer-info-item"><label>Life Time Volume</label><p className="sq-value">{getDisplayValue(project.lifetimeVolume || project.lifeTimeVolume)}</p></div>
                  <div className="sq-buyer-info-item"><label>Target Cost</label><p className="sq-value">{getDisplayValue(project.targetCost || basePrice.targetCost || basePrice.price || basePrice.rangeMin)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-items-requested-standalone">
          <div className="sq-items-requested-card">
            <div className="sq-items-requested-header" onClick={() => setProductsExpanded((v) => !v)}>
              <div className="sq-items-requested-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <path d="m3.3 7 8.7 5 8.7-5"></path>
                  <path d="M12 22V12"></path>
                </svg>
                <h3 className="sq-section-title">Products to be Manufactured</h3>
              </div>
              <span className={`sq-items-requested-chevron ${productsExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {productsExpanded && (
              <div className="sq-items-requested-content">
                {products.length > 0 ? (
                  products.map((row, idx) => (
                    <div
                      key={`prod-${idx}`}
                      style={{
                        background: '#FFFFFF',
                        border: '1px solid #E0E0E0',
                        borderRadius: '12px',
                        padding: '12px',
                        marginBottom: '10px',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '10px', color: '#0B1320' }}>
                        Item #{idx + 1}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '12px' }}>
                        <div className="sq-items-requested-item">
                          <label>Part Name</label>
                          <p className="sq-value">{getDisplayValue(row?.partName)}</p>
                        </div>
                        <div className="sq-items-requested-item">
                          <label>Part #</label>
                          <p className="sq-value">{getDisplayValue(row?.partNumber)}</p>
                        </div>
                        <div className="sq-items-requested-item">
                          <label>Rev</label>
                          <p className="sq-value">{getDisplayValue(row?.rev)}</p>
                        </div>
                        <div className="sq-items-requested-item">
                          <label>UOM</label>
                          <p className="sq-value">{resolveUomLabel(row?.uom)}</p>
                        </div>
                        <div className="sq-items-requested-item">
                          <label>Quantity</label>
                          <p className="sq-value">{getDisplayValue(row?.quantity)}</p>
                        </div>
                        <div className="sq-items-requested-item">
                          <label>Material</label>
                          <p className="sq-value">{getDisplayValue(row?.material)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="sq-help-text">No products added.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setTechnicalExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M12 2v20"></path>
                  <path d="M2 12h20"></path>
                  <path d="m4.93 4.93 14.14 14.14"></path>
                  <path d="m19.07 4.93-14.14 14.14"></path>
                </svg>
                <h3 className="sq-section-title">Technical Requirements</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${technicalExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {technicalExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Materials Detail</label><p className="sq-value">{getDisplayValue(technical.materialsDetail)}</p></div>
                  <div className="sq-buyer-info-item"><label>Heat Treatment</label><p className="sq-value">{getDisplayValue(technical.heatTreatment)}</p></div>
                  <div className="sq-buyer-info-item"><label>Coating / Surface Finish</label><p className="sq-value">{getDisplayValue(technical.coatingSurfaceFinish)}</p></div>

                  <div className="sq-buyer-info-item"><label>Hardness Target</label><p className="sq-value">{getDisplayValue(technical.hardnessTarget)}</p></div>
                  <div className="sq-buyer-info-item"><label>Welding Requirement</label><p className="sq-value">{getDisplayValue(technical.weldingRequirement)}</p></div>
                  <div className="sq-buyer-info-item"><label>NDT</label><p className="sq-value">{getDisplayValue(technical.ndt)}</p></div>

                  <div className="sq-buyer-info-item"><label>Resin Polymer Grade</label><p className="sq-value">{getDisplayValue(technical.resinPolymerGrade)}</p></div>
                  <div className="sq-buyer-info-item"><label>Colour Code</label><p className="sq-value">{getDisplayValue(technical.colourCode)}</p></div>
                  <div className="sq-buyer-info-item"><label>PCB Finish</label><p className="sq-value">{getDisplayValue(technical.pcbStackupFinish)}</p></div>

                  <div className="sq-buyer-info-item"><label>Clean Room Class</label><p className="sq-value">{getDisplayValue(technical.cleanRoomClass)}</p></div>
                  <div className="sq-buyer-info-item"><label>Target Shelf Life</label><p className="sq-value">{getDisplayValue(technical.targetShelfLifeMonths ? `${technical.targetShelfLifeMonths} months` : '')}</p></div>
                  <div className="sq-buyer-info-item"><label>Formula Recipe</label><p className="sq-value">{getDisplayValue(technical.formulaRecipe || technical.formulaRecipeRequired)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setQualityExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M20 13c0 5-3.5 7.5-8 10-4.5-2.5-8-5-8-10V6l8-3 8 3z"></path>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
                <h3 className="sq-section-title">Quality & Compliance</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${qualityExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {qualityExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Certificates Required</label><p className="sq-value">{getDisplayValue(quality.certificateRequired)}</p></div>
                  <div className="sq-buyer-info-item"><label>Regulatory Claims</label><p className="sq-value">{getDisplayValue(quality.regulatoryClaims)}</p></div>
                  <div className="sq-buyer-info-item"><label>Acceptable Quality Level</label><p className="sq-value">{getDisplayValue(quality.acceptableQualityLevel)}</p></div>
                  <div className="sq-buyer-info-item"><label>Inspection Document</label><p className="sq-value">{getDisplayValue(quality.inspectionRequiredDocument)}</p></div>
                  <div className="sq-buyer-info-item"><label>Test Methods / Standards</label><p className="sq-value">{getDisplayValue(quality.testMethodsStandards)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setPackagingExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <path d="m3.3 7 8.7 5 8.7-5"></path>
                  <path d="M12 22V12"></path>
                </svg>
                <h3 className="sq-section-title">Packaging & Logistics</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${packagingExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {packagingExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Packaging Specification</label><p className="sq-value">{getDisplayValue(packaging.packagingSpecification)}</p></div>
                  <div className="sq-buyer-info-item"><label>Labeling & Reworking</label><p className="sq-value">{getDisplayValue(packaging.labellingBarcoding)}</p></div>
                  <div className="sq-buyer-info-item"><label>Ship To</label><p className="sq-value">{getDisplayValue(packaging.shipToCountryPostcode)}</p></div>
                  <div className="sq-buyer-info-item"><label>Transport Mode</label><p className="sq-value">{resolveTransportLabel(packaging.preferredTransport)}</p></div>
                  <div className="sq-buyer-info-item"><label>Cold Chain Required</label><p className="sq-value">{getDisplayValue(packaging.coldChain)}</p></div>
                  <div className="sq-buyer-info-item"><label>Test Methods / Standards</label><p className="sq-value">{getDisplayValue(packaging.packagingTestMethods)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setCommercialExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M20 7H4"></path>
                  <path d="M20 12H4"></path>
                  <path d="M20 17H4"></path>
                  <path d="M8 7v10"></path>
                  <path d="M16 7v10"></path>
                </svg>
                <h3 className="sq-section-title">Commercial & Legal Requirements</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${commercialExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {commercialExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Preferred Pricing Model</label><p className="sq-value">{getDisplayValue(commercial.preferredPricingModel)}</p></div>
                  <div className="sq-buyer-info-item"><label>Payment Method</label><p className="sq-value">{resolvePaymentMethodLabel(commercial.paymentMethod)}</p></div>
                  <div className="sq-buyer-info-item"><label>Payment Terms</label><p className="sq-value">{resolvePaymentTermsLabel(commercial.paymentTerms)}</p></div>

                  <div className="sq-buyer-info-item"><label>Price Validity Days</label><p className="sq-value">{getDisplayValue(commercial.priceValidityDays)}</p></div>
                  <div className="sq-buyer-info-item"><label>Incoterms</label><p className="sq-value">{resolveIncotermsLabel(commercial.incoterms)}</p></div>
                  <div className="sq-buyer-info-item"><label>Is Price Negotiable</label><p className="sq-value">{formatYesNo(commercial.priceNegotiable)}</p></div>

                  <div className="sq-buyer-info-item"><label>Liquidated Damages</label><p className="sq-value">{getDisplayValue(commercial.liquidatedDamages)}</p></div>
                  <div className="sq-buyer-info-item"><label>Tooling Ownership</label><p className="sq-value">{getDisplayValue(commercial.toolingOwnership)}</p></div>
                  <div className="sq-buyer-info-item"><label>Jurisdiction</label><p className="sq-value">{getDisplayValue(commercial.jurisdiction)}</p></div>

                  <div className="sq-buyer-info-item"><label>Port of Shipping</label><p className="sq-value">{getDisplayValue(commercial.portOfShipping)}</p></div>
                  <div className="sq-buyer-info-item"><label>Expected Dispatch Date</label><p className="sq-value">{getDisplayValue(commercial.expectedDispatchDate)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setBasePriceExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M12 1v22"></path>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <h3 className="sq-section-title">Base Price</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${basePriceExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {basePriceExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Price</label><p className="sq-value">{getDisplayValue(basePrice.price)}</p></div>
                  <div className="sq-buyer-info-item"><label>Currency</label><p className="sq-value">{getDisplayValue(basePrice.currency)}</p></div>
                  <div className="sq-buyer-info-item"><label>MOQ</label><p className="sq-value">{getDisplayValue(basePrice.moq)}</p></div>
                  <div className="sq-buyer-info-item"><label>CGST</label><p className="sq-value">{getDisplayValue(basePrice.cgst)}</p></div>
                  <div className="sq-buyer-info-item"><label>SGST</label><p className="sq-value">{getDisplayValue(basePrice.sgst)}</p></div>
                  <div className="sq-buyer-info-item"><label>IGST</label><p className="sq-value">{getDisplayValue(basePrice.igst)}</p></div>
                  <div className="sq-buyer-info-item"><label>CESS</label><p className="sq-value">{getDisplayValue(basePrice.cess)}</p></div>
                  <div className="sq-buyer-info-item"><label>VAT</label><p className="sq-value">{getDisplayValue(basePrice.vat)}</p></div>
                  <div className="sq-buyer-info-item"><label>Other Taxes</label><p className="sq-value">{getDisplayValue(basePrice.otherTaxes)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setTierBreaksExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M3 6h18"></path>
                  <path d="M3 12h18"></path>
                  <path d="M3 18h18"></path>
                  <path d="M8 6v12"></path>
                  <path d="M16 6v12"></path>
                </svg>
                <h3 className="sq-section-title">Preferred Tier Breaks</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${tierBreaksExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {tierBreaksExpanded && (
              <div className="sq-buyer-info-content">
                {tierBreaks.length > 0 ? (
                  <div className="sq-table-wrapper">
                    <table className="sq-table">
                      <thead>
                        <tr>
                          <th>Tier Label</th>
                          <th>Min Quantity</th>
                          <th>Unit Price</th>
                          <th>Currency</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tierBreaks.map((row, idx) => (
                          <tr key={`tier-${idx}`}>
                            <td>{getDisplayValue(row?.tierLabel)}</td>
                            <td>{getDisplayValue(row?.minQuantity)}</td>
                            <td>{getDisplayValue(row?.unitPrice)}</td>
                            <td>{getDisplayValue(row?.currency)}</td>
                            <td>{getDisplayValue(row?.remarks)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="sq-help-text">No preferred tier breaks provided.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setSampleExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M10 2h4"></path>
                  <path d="M12 2v6"></path>
                  <path d="M8 8h8"></path>
                  <path d="M7 8v11a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3V8"></path>
                </svg>
                <h3 className="sq-section-title">Sample Requirements</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${sampleExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {sampleExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Sample Offered</label><p className="sq-value">{formatYesNo(sample.sampleOffered)}</p></div>
                  <div className="sq-buyer-info-item"><label>Sample Type</label><p className="sq-value">{getDisplayValue(sample.sampleType)}</p></div>
                  <div className="sq-buyer-info-item"><label>Sample Cost</label><p className="sq-value">{getDisplayValue(sample.sampleCost)}</p></div>
                  <div className="sq-buyer-info-item"><label>Sample Price</label><p className="sq-value">{getDisplayValue(sample.samplePrice)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-buyer-info-standalone">
          <div className="sq-buyer-info-card">
            <div className="sq-buyer-info-header" onClick={() => setWarrantyExpanded((v) => !v)}>
              <div className="sq-buyer-info-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-section-icon">
                  <path d="M12 2l7 4v6c0 5-3.5 7.5-7 10-3.5-2.5-7-5-7-10V6l7-4z"></path>
                  <path d="M9 12l2 2 4-4"></path>
                </svg>
                <h3 className="sq-section-title">Warranty & Guarantee Requirements</h3>
              </div>
              <span className={`sq-buyer-info-chevron ${warrantyExpanded ? 'sq-chevron-expanded' : 'sq-chevron-collapsed'}`}>⌃</span>
            </div>
            {warrantyExpanded && (
              <div className="sq-buyer-info-content">
                <div className="sq-buyer-info-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                  <div className="sq-buyer-info-item"><label>Warranty</label><p className="sq-value">{getDisplayValue(sample.warranty)}</p></div>
                  <div className="sq-buyer-info-item"><label>Warranty Period</label><p className="sq-value">{getDisplayValue(sample.warrantyPeriod)}</p></div>
                  <div className="sq-buyer-info-item"><label>Guarantee</label><p className="sq-value">{getDisplayValue(sample.guarantee)}</p></div>
                  <div className="sq-buyer-info-item"><label>Warranty Remarks</label><p className="sq-value">{getDisplayValue(sample.warrantyRemarks)}</p></div>
                  <div className="sq-buyer-info-item"><label>Guarantee Period</label><p className="sq-value">{getDisplayValue(sample.guaranteePeriod)}</p></div>
                  <div className="sq-buyer-info-item"><label>Guarantee Remarks</label><p className="sq-value">{getDisplayValue(sample.guaranteeRemarks)}</p></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sq-quotation-response-header">
          <div className="sq-card-title-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sq-icon sq-file-icon" aria-hidden="true">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M10 9H8"></path>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
            </svg>
            <h2 className="sq-card-title">Your Quotation Response</h2>
          </div>
        </div>
        <div
          style={{
            background: '#FFF7EF',
            border: '1px solid #F6D8B8',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '12px',
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#8A3C00', lineHeight: 1.2 }}>
            Submit Your Quotation
          </div>
          <div style={{ marginTop: '6px', fontSize: '16px', color: '#B45309' }}>
            Fill in the details below to submit your quotation for this RFQ
          </div>
        </div>

        <div className="sq-section">
          <h3 className="sq-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#F97316', fontWeight: 800 }}>⚖</span>
            Commercial & Legal
          </h3>
          <div className="sq-grid-4">
            <div className="sq-field">
              <label>Preferred Pricing Model</label>
              <Select
                className="input-pill p-0"
                options={PRICING_MODEL_OPTIONS}
                value={findOption(PRICING_MODEL_OPTIONS, commercialEdit.preferredPricingModel)}
                placeholder="Select model"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, preferredPricingModel: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Payment Method</label>
              <Select
                className="input-pill p-0"
                options={paymentMethodOptions}
                value={findOption(paymentMethodOptions, commercialEdit.paymentMethod)}
                placeholder="Select method"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, paymentMethod: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Unit of Measurement</label>
              <Select
                className="input-pill p-0"
                options={unitOfMeasurementOptions}
                value={findOption(unitOfMeasurementOptions, commercialEdit.unitOfMeasurement)}
                placeholder="Select unit"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, unitOfMeasurement: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Payment Terms</label>
              <Select
                className="input-pill p-0"
                options={paymentScheduleOptions}
                value={findOption(paymentScheduleOptions, commercialEdit.paymentTerms)}
                placeholder="Select terms"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, paymentTerms: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>

            <div className="sq-field">
              <label>Price Validity (Days)</label>
              <input type="number" min="0" step="1" className="input-control" value={commercialEdit.priceValidityDays} onChange={(e) => setCommercialEdit((prev) => ({ ...prev, priceValidityDays: e.target.value }))} placeholder="e.g., 30" />
            </div>
            <div className="sq-field">
              <label>Incoterms</label>
              <Select
                className="input-pill p-0"
                options={shippingTermsOptions}
                value={findOption(shippingTermsOptions, commercialEdit.incoterms)}
                placeholder="Select incoterm"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, incoterms: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Price Escalation Basis</label>
              <Select
                className="input-pill p-0"
                options={PRICE_ESCALATION_OPTIONS}
                value={findOption(PRICE_ESCALATION_OPTIONS, commercialEdit.priceEscalationClause)}
                placeholder="Select basis"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, priceEscalationClause: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Add on Categories</label>
              <Select
                className="input-pill p-0"
                isMulti
                closeMenuOnSelect={false}
                options={ADD_ON_CATEGORY_OPTIONS}
                value={findMultiOptions(ADD_ON_CATEGORY_OPTIONS, commercialEdit.addOnCategories)}
                placeholder="Select categories"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, addOnCategories: selected ? selected.map((s) => s.value) : [] }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>

            <div className="sq-field">
              <label>Liquidated Damage/ Delay Penalty</label>
              <input className="input-control" value={commercialEdit.liquidatedDamages} onChange={(e) => setCommercialEdit((prev) => ({ ...prev, liquidatedDamages: e.target.value }))} placeholder="e.g., 1% per week" />
            </div>
            <div className="sq-field">
              <label>Negotiable</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '42px' }}>
                <YesNoSwitch id="cm-negotiable" checked={!!commercialEdit.priceNegotiable} onToggle={(next) => setCommercialEdit((prev) => ({ ...prev, priceNegotiable: next }))} />
              </div>
            </div>
            <div className="sq-field">
              <label>Tooling / NRE Expected</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '42px' }}>
                <YesNoSwitch id="cm-tooling" checked={!!commercialEdit.toolingNrePossible} onToggle={(next) => setCommercialEdit((prev) => ({ ...prev, toolingNrePossible: next }))} />
              </div>
            </div>
            <div className="sq-field">
              <label>Tooling IME</label>
              <input className="input-control" value={commercialEdit.toolingNre} onChange={(e) => setCommercialEdit((prev) => ({ ...prev, toolingNre: e.target.value }))} placeholder="Tooling details" />
            </div>

            <div className="sq-field">
              <label>Tooling Owner Ship</label>
              <Select
                className="input-pill p-0"
                options={TOOLING_OWNERSHIP_OPTIONS}
                value={findOption(TOOLING_OWNERSHIP_OPTIONS, commercialEdit.toolingOwnership)}
                placeholder="Select ownership"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, toolingOwnership: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Jurisdiction / Governing Law</label>
              <Select
                className="input-pill p-0"
                options={JURISDICTION_OPTIONS}
                value={findOption(JURISDICTION_OPTIONS, commercialEdit.jurisdiction)}
                placeholder="Select jurisdiction"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, jurisdiction: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Delivery Method</label>
              <Select
                className="input-pill p-0"
                options={DELIVERY_METHOD_OPTIONS}
                value={findOption(DELIVERY_METHOD_OPTIONS, commercialEdit.deliveryMethods)}
                placeholder="Select method"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setCommercialEdit((prev) => ({ ...prev, deliveryMethods: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Port of Shipping</label>
              <input className="input-control" value={commercialEdit.portOfShipping} onChange={(e) => setCommercialEdit((prev) => ({ ...prev, portOfShipping: e.target.value }))} placeholder="Shipping port" />
            </div>

            <div className="sq-field">
              <label>Expected Dispatch Date</label>
              <input type="date" className="input-control" value={commercialEdit.expectedDispatchDate} onChange={(e) => setCommercialEdit((prev) => ({ ...prev, expectedDispatchDate: e.target.value }))} />
            </div>
          </div>
        </div>

        <div className="sq-section sq-compact-section">
          <h3 className="sq-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#F97316', fontWeight: 800 }}>$</span>
            Base Pricing
          </h3>
          <div className="sq-base-pricing-grid">
            <div className="sq-field">
              <label>Enter Price $</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input-control"
                value={basePriceEdit.price}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, price: e.target.value }))}
                placeholder="$ 0.00"
              />
            </div>
            <div className="sq-field">
              <label>Quantity</label>
              <input
                type="number"
                min="0"
                step="1"
                className="input-control"
                value={basePriceEdit.quantity}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, quantity: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="sq-field">
              <label>CGST%</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input-control"
                value={basePriceEdit.cgst}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, cgst: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="sq-field">
              <label>SGST%</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input-control"
                value={basePriceEdit.sgst}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, sgst: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="sq-field">
              <label>IGST%</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input-control"
                value={basePriceEdit.igst}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, igst: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="sq-field">
              <label>CESS%</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input-control"
                value={basePriceEdit.cess}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, cess: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="sq-field">
              <label>VAT%</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input-control"
                value={basePriceEdit.vat}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, vat: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="sq-field">
              <label>Other Taxes%</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input-control"
                value={basePriceEdit.otherTaxes}
                onChange={(e) => setBasePriceEdit((prev) => ({ ...prev, otherTaxes: e.target.value }))}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className="sq-section sq-compact-section">
          <div className="sq-tier-breaks-header">
            <h3 className="sq-section-title">Preferred Tier breaks</h3>
            <button type="button" className="sq-tier-add-btn" onClick={addTierBreakRow}>+ Add</button>
          </div>
          {tierBreaksEdit.map((row, idx) => (
            <div key={`tier-break-edit-${idx}`} className="sq-tier-breaks-grid">
              <div className="sq-field">
                <label>Tier</label>
                <input
                  className="input-control"
                  value={row.tierLabel}
                  onChange={(e) => handleTierBreakChange(idx, 'tierLabel', e.target.value)}
                  placeholder="Tier 1"
                />
              </div>
              <div className="sq-field">
                <label>Minimum Quantity</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="input-control"
                  value={row.minQuantity}
                  onChange={(e) => handleTierBreakChange(idx, 'minQuantity', e.target.value)}
                  placeholder="Min qty"
                />
              </div>
              <div className="sq-field">
                <label>Target Unit Price</label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="input-control"
                  value={row.unitPrice}
                  onChange={(e) => handleTierBreakChange(idx, 'unitPrice', e.target.value)}
                  placeholder="Price"
                />
              </div>
              <div className="sq-field">
                <label>Currency</label>
                <select
                  className="input-control input-control-select"
                  value={row.currency}
                  onChange={(e) => handleTierBreakChange(idx, 'currency', e.target.value)}
                >
                  {currencyMappedOptions.map((opt) => (
                    <option key={`tb-currency-${String(opt.value)}`} value={String(opt.value)}>{String(opt.label)}</option>
                  ))}
                </select>
              </div>
              <div className="sq-field sq-tier-remarks-field">
                <label>Remarks If Any</label>
                <input
                  className="input-control"
                  value={row.remarks}
                  onChange={(e) => handleTierBreakChange(idx, 'remarks', e.target.value)}
                  placeholder="Remarks"
                />
              </div>
              <div className="sq-tier-remove-wrap">
                <button
                  type="button"
                  className="sq-tier-remove-btn"
                  onClick={() => removeTierBreakRow(idx)}
                  disabled={tierBreaksEdit.length <= 1}
                  aria-label="Remove tier row"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="sq-section sq-compact-section">
          <h3 className="sq-section-title">Sample details</h3>
          <div className="sq-sample-grid">
            <div className="sq-field">
              <label>Sample Offered</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '42px' }}>
                <YesNoSwitch id="sq-sample-offered" checked={!!sampleQuoteEdit.sampleOffered} onToggle={(next) => setSampleQuoteEdit((prev) => ({ ...prev, sampleOffered: next }))} />
              </div>
            </div>
            <div className="sq-field">
              <label>Sample Type</label>
              <Select
                className="input-pill p-0"
                options={SAMPLE_TYPE_OPTIONS}
                value={findOption(SAMPLE_TYPE_OPTIONS, sampleQuoteEdit.sampleType)}
                placeholder="Select"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setSampleQuoteEdit((prev) => ({ ...prev, sampleType: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Sample Cost</label>
              <Select
                className="input-pill p-0"
                options={SAMPLE_COST_OPTIONS}
                value={findOption(SAMPLE_COST_OPTIONS, sampleQuoteEdit.sampleCost)}
                placeholder="Select"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setSampleQuoteEdit((prev) => ({ ...prev, sampleCost: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Price$</label>
              <input type="number" min="0" step="any" className="input-control" value={sampleQuoteEdit.samplePrice} onChange={(e) => setSampleQuoteEdit((prev) => ({ ...prev, samplePrice: e.target.value }))} placeholder="Price" />
            </div>
            <div className="sq-field">
              <label>Cur.</label>
              <Select
                className="input-pill p-0"
                options={currencyMappedOptions}
                value={findOption(currencyMappedOptions, sampleQuoteEdit.sampleCurrency)}
                placeholder="Currency"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setSampleQuoteEdit((prev) => ({ ...prev, sampleCurrency: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Dispatch Lead Time (Days)</label>
              <input type="number" min="0" step="1" className="input-control" value={sampleQuoteEdit.dispatchLeadTimeDays} onChange={(e) => setSampleQuoteEdit((prev) => ({ ...prev, dispatchLeadTimeDays: e.target.value }))} placeholder="Days" />
            </div>
            <div className="sq-field">
              <label>Sample Shipping Mode</label>
              <Select
                className="input-pill p-0"
                options={SAMPLE_SHIPPING_MODE_OPTIONS}
                value={findOption(SAMPLE_SHIPPING_MODE_OPTIONS, sampleQuoteEdit.sampleShippingMode)}
                placeholder="Select mode"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setSampleQuoteEdit((prev) => ({ ...prev, sampleShippingMode: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Sample Return Policy</label>
              <Select
                className="input-pill p-0"
                options={SAMPLE_RETURN_POLICY_OPTIONS}
                value={findOption(SAMPLE_RETURN_POLICY_OPTIONS, sampleQuoteEdit.sampleReturnPolicy)}
                placeholder="Select policy"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setSampleQuoteEdit((prev) => ({ ...prev, sampleReturnPolicy: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Remarks / Instructions</label>
              <input className="input-control" value={sampleQuoteEdit.sampleRemarks} onChange={(e) => setSampleQuoteEdit((prev) => ({ ...prev, sampleRemarks: e.target.value }))} placeholder="Sample instructions" />
            </div>
          </div>
        </div>

        <div className="sq-section sq-compact-section">
          <h3 className="sq-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#F97316', fontWeight: 800 }}>⚲</span>
            Warranty & Guarantee
          </h3>
          <div className="sq-warranty-grid">
            <div className="sq-field">
              <label>Warranty</label>
              <input type="number" min="0" step="1" className="input-control" value={sampleQuoteEdit.warranty} onChange={(e) => setSampleQuoteEdit((prev) => ({ ...prev, warranty: e.target.value }))} placeholder="e.g. 12" />
            </div>
            <div className="sq-field">
              <label>Period of Warranty</label>
              <Select
                className="input-pill p-0"
                options={WARRANTY_PERIOD_OPTIONS}
                value={findOption(WARRANTY_PERIOD_OPTIONS, sampleQuoteEdit.warrantyPeriod)}
                placeholder="Select period"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setSampleQuoteEdit((prev) => ({ ...prev, warrantyPeriod: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Remarks</label>
              <input className="input-control" value={sampleQuoteEdit.warrantyRemarks} onChange={(e) => setSampleQuoteEdit((prev) => ({ ...prev, warrantyRemarks: e.target.value }))} placeholder="Warranty remarks" />
            </div>
            <div className="sq-field">
              <label>Guarantee</label>
              <input className="input-control" value={sampleQuoteEdit.guarantee} onChange={(e) => setSampleQuoteEdit((prev) => ({ ...prev, guarantee: e.target.value }))} placeholder="Guarantee details" />
            </div>
            <div className="sq-field">
              <label>Period of Guarantee</label>
              <Select
                className="input-pill p-0"
                options={WARRANTY_PERIOD_OPTIONS}
                value={findOption(WARRANTY_PERIOD_OPTIONS, sampleQuoteEdit.guaranteePeriod)}
                placeholder="Select period"
                isClearable={false}
                isSearchable={false}
                onChange={(selected) => setSampleQuoteEdit((prev) => ({ ...prev, guaranteePeriod: selected ? selected.value : '' }))}
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={mapSelectStyles}
              />
            </div>
            <div className="sq-field">
              <label>Remarks</label>
              <input className="input-control" value={sampleQuoteEdit.guaranteeRemarks} onChange={(e) => setSampleQuoteEdit((prev) => ({ ...prev, guaranteeRemarks: e.target.value }))} placeholder="Guarantee remarks" />
            </div>
          </div>
        </div>

        <div className="sq-section sq-compact-section">
          <h3 className="sq-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#F97316', fontWeight: 800 }}>⤴</span>
            Attachments
          </h3>
          <div className="sq-attachment-upload-label">Upload Product Image</div>
          <div className="sq-attachment-dropzone">
            <div className="sq-attachment-dropzone-icon">⤴</div>
            <div className="sq-attachment-dropzone-text">Click or drag files/Media to upload</div>
            <div className="sq-attachment-dropzone-hint">Max Media/Dm.mp4 Format only</div>
            <label className="sq-attachment-browse-btn">
              Browse Files
              <input type="file" multiple className="sq-attachment-file-input" onChange={handleQuoteAttachmentSelect} />
            </label>
          </div>
          {quoteAttachmentNames.length > 0 ? (
            <div className="sq-attached-files">
              {quoteAttachmentNames.map((name, idx) => (
                <span key={`quote-upload-${idx}`} className="sq-file-item">{name}</span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="sq-section sq-declaration-card sq-compact-section">
          <h3 className="sq-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#F97316', fontWeight: 800 }}>ⓘ</span>
            Supplier Declaration
          </h3>
          <div className="sq-declaration-note">
            <strong>Declaration:</strong> We herby confirm that all quoted prices are final and in complince with applicable laws and taxes.
            All information provided in this quotation is accurate and complete to the best of our knowledge. We understand that any
            misrepresentation may lead to disqualification and legal action.
          </div>

          <label className="sq-declaration-check">
            <input
              id="decTerms"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <div className="sq-declaration-check-content">
              <div className="sq-declaration-check-title">I agree to the Terms & Conditions and Platform Policy</div>
              <div className="sq-declaration-check-subtitle">
                By checking this box, you acknowledge that you have read and agree to abide by all terms and conditions.
              </div>
            </div>
          </label>

          <label className="sq-declaration-check">
            <input
              id="decComp"
              type="checkbox"
              checked={complianceAccepted}
              onChange={(e) => setComplianceAccepted(e.target.checked)}
            />
            <div className="sq-declaration-check-content">
              <div className="sq-declaration-check-title">I confirm product origin and quality compliance</div>
              <div className="sq-declaration-check-subtitle">
                You confirm that all products meet the specified quality standards and comply with country of origin declarations.
              </div>
            </div>
          </label>

          {(!termsAccepted || !complianceAccepted) ? (
            <div className="sq-declaration-warning">Both declarations must be confirmed before submitting your Quotation</div>
          ) : null}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {Object.keys(fieldErrors).length > 0 ? (
            <div className="sq-help-text" style={{ color: '#b91c1c', marginBottom: '10px' }}>
              {Object.values(fieldErrors)[0]}
            </div>
          ) : null}
          <div className="sq-footer">
            <div className="sq-footer-left">
              <Link to="/seller/cm-rfqs" className="sq-footer-back-btn">
                ← Back to RFQ list
              </Link>
            </div>
            <div className="sq-footer-right">
              {!existingQuote ? (
                <button type="submit" className="sq-footer-submit-btn" disabled={saving || !termsAccepted || !complianceAccepted}>
                  {saving ? 'Saving…' : 'Submit Quotation'}
                </button>
              ) : null}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
